import { useState, useMemo, memo } from "react";

import { cn, formatTimestamp } from "@/lib/utils";
import {
  User,
  Wrench,
  FileText,
  AlertCircle,
  Loader2,
  FileIcon,
  RefreshCw,
  Bot,
  Check,
  Minus,
  ChevronDown,
  ChevronRight,
  Expand,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { MessageType, ToolStatus, TaskStatus } from "@optidev/shared-types";

import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  extractUIPayload,
  parseVisualEditorMessage,
  getElementSummary,
} from "@/lib/agent/payload-parser";
import { ClaudeResponseCard } from "./ClaudeResponseCard";
import { ThinkingIndicator } from "./thinking-indicator";
import {
  OptiDevCloudActivating,
  type ActivationStatus,
} from "./optidev-cloud-activating";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { SkillCard } from "./tools";

/** Must match sidecar MARKETPLACE_PREFIX and gateway SKILL_FOLDER_PREFIX */
const SKILL_PREFIX = "optidev-market-";
import { MentionBadge } from "./mention-badge";
import { PhaseGroup } from "./PhaseGroup";
import { groupSegmentsIntoPhases, phaseGroupingEnabled } from "./phase-grouping";
import type { Phase } from "./phase-grouping";
import type { ThemeContent } from "@/types/theme.types";

export interface FileAttachment {
  id: string;
  refId: string;
  originalName: string;
  slug: string;
  assetUrl: string;
  thumbnailUrl?: string;
  downloadUrl?: string;
  fileSize: number;
  mimeType: string;
  intent: string;
}

// Active tool being executed (for real-time display)
export interface ActiveTool {
  entryId: string;
  toolUseId: string;
  toolName: string;
  toolInput?: Record<string, unknown>;
  status: ToolStatus;
  errorMessage?: string;
  resultSummary?: string; // Brief summary like "150 lines" or "234 chars"
  startedAt: Date;
  completedAt?: Date;
  textPosition?: number; // Character position in text when tool started (for inline display)
  // Theme HITL card anchored to this tool, rendered inline directly beneath it.
  // Set by AgentChat when a theme_approval/theme_gallery ui_payload message is
  // matched to its producing generate_theme_hitl tool (works live + on reload).
  hitlPayloadContent?: string; // raw <ui_payload>…</ui_payload> blob
  hitlMessageId?: string; // id of the source HITL message (passed as ClaudeResponseCard messageId)
  hitlMessageTimestamp?: Date; // source HITL message timestamp (theme-card supersession check, #54)
  // Inline screenshot (base64) captured by capture_url, rendered beneath the tool.
  imageBase64?: string;
  imageMimeType?: string;
}

// Re-export for convenience
export { MessageType, ToolStatus, TaskStatus };

export interface Message {
  id: string;
  taskId?: string; // Task ID for lookup (persists after id becomes assistantMessageId)
  entryId?: string; // Database entry ID (set when message is persisted to backend)
  type: MessageType;
  content: string;
  metadata?: {
    toolName?: string;
    files?: string[];
    duration?: number;
  };
  timestamp: Date;
  files?: FileAttachment[]; // Linked files from database
  toolUsage?: {
    Read?: string[];
    Write?: string[];
    Edit?: string[];
  };
  activeTools?: ActiveTool[]; // Tools currently being executed (real-time tracking)
  status?: TaskStatus; // Task status
  isLoading?: boolean; // Placeholder loading state before Worker starts
  clickedActions?: string[]; // IDs of HITL action buttons that have been clicked
  isStale?: boolean; // True if this message's actions should be disabled (e.g., old approve_plan after user requested changes)
  parentEntryId?: string; // User message entry ID this assistant message responds to (for visual grouping)
  userMessageId?: string | null; // The triggering user message ID for turn-based grouping (all assistant responses in a turn share this)
  queuedReason?: string; // Reason task is queued (e.g., 'container_paused') - for UI indicator
  activation?: ActivationStatus; // OptiDev Cloud activation progress (renders the staged progress card)
  stopped?: boolean; // True if the user stopped this turn mid-flight. Stop forces status → COMPLETED, so this flag is what distinguishes a halt from a natural finish (e.g. so we don't mislabel the interrupted phase as a closing "Wrap-up").
}

interface AgentMessageProps {
  message: Message;
  onOpenFile?: (filePath: string) => void;
  onAction?: (actionId: string, messageId: string) => void;
  showHeader?: boolean; // Hide icon/timestamp for consecutive assistant messages
  /**
   * Reliable session-level "this turn is still streaming" signal from AgentChat,
   * set only on the last assistant message of the active turn. The per-message
   * `status` isn't dependable during a stream (the merged/reconstructed message
   * can lack RUNNING), so the live phase + shimmer keys off this instead.
   */
  isStreamingTurn?: boolean;
  /**
   * True when this turn ends by handing control back to the user — the next
   * message is a question / HITL card awaiting their input. Flips the trailing
   * closing-phase label from "Wrap-up" to "Over to you". Computed by AgentChat,
   * which alone can see the following message.
   */
  awaitsUserInput?: boolean;
  // Theme preview props (required for ThemePreviewCard in ClaudeResponseCard)
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  projectId: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onBrowseThemes?: () => void;
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
  onCacheTtlSet?: (path: string, ttl: number) => void;
  // Media library props
  devContainerId?: string;
  previewUrl?: string;
}

/**
 * Get a shortened display name for a tool
 */
function getToolDisplayName(
  toolName: string,
  toolInput?: Record<string, unknown>,
): string {
  // Remove common prefixes
  if (toolName.startsWith("mcp__")) {
    const parts = toolName.split("__");
    return parts[parts.length - 1];
  }
  // Show skill name for Skill tool calls
  if (toolName === "Skill" && toolInput) {
    const skillName = toolInput.skill || toolInput.name || toolInput.skill_name;
    if (typeof skillName === "string") {
      return `Skill: ${skillName}`;
    }
  }
  return toolName;
}

/**
 * Get the most relevant detail string for a tool (file path, URL, query) from its input.
 * WebFetch → url, WebSearch → query, file tools → file_path.
 */
function getToolFilePath(
  toolName: string,
  toolInput?: Record<string, unknown>,
): string | null {
  if (!toolInput) return null;

  // Tool-specific keys (web tools surface URL / query instead of a file path)
  if (toolName === "WebFetch" && typeof toolInput.url === "string") {
    return toolInput.url as string;
  }
  if (toolName === "WebSearch" && typeof toolInput.query === "string") {
    return toolInput.query as string;
  }
  if (toolName === "mcp__0__fetch_webpage" && typeof toolInput.url === "string") {
    return toolInput.url as string;
  }

  // Default: common file-path parameter names
  const pathKeys = ["file_path", "path", "relative_path", "filePath"];
  for (const key of pathKeys) {
    if (toolInput[key] && typeof toolInput[key] === "string") {
      return toolInput[key] as string;
    }
  }
  return null;
}

/**
 * Shorten a path or URL to a compact display form.
 * URLs → hostname + pathname (no `.../` prefix).
 * File paths → last 2 segments with `.../` prefix when deeper than 2 levels.
 */
function shortenPath(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    try {
      const u = new URL(path);
      const pathname = u.pathname === "/" ? "" : u.pathname;
      return u.hostname + pathname;
    } catch {
      return path;
    }
  }
  const segments = path.split("/").filter(Boolean);
  if (segments.length <= 2) {
    return segments.join("/");
  }
  return ".../" + segments.slice(-2).join("/");
}

/**
 * Categorize a tool by its name
 */
function getToolCategory(toolName: string): "read" | "edit" | "bash" | "other" {
  const name = toolName.toLowerCase();
  if (
    name.includes("read") ||
    name.includes("glob") ||
    name.includes("grep") ||
    name.includes("search") ||
    name.includes("find")
  ) {
    return "read";
  }
  if (name.includes("write") || name.includes("edit")) {
    return "edit";
  }
  if (
    name.includes("bash") ||
    name.includes("shell") ||
    name.includes("exec")
  ) {
    return "bash";
  }
  return "other";
}

/**
 * Get summary label for a tool group
 */
function getToolGroupSummary(tools: ActiveTool[]): string {
  if (tools.length === 0) return "";

  const categories = tools.map((t) => getToolCategory(t.toolName));
  const readCount = categories.filter((c) => c === "read").length;
  const editCount = categories.filter((c) => c === "edit").length;
  const bashCount = categories.filter((c) => c === "bash").length;
  const otherCount = categories.filter((c) => c === "other").length;

  const parts: string[] = [];
  if (readCount > 0)
    parts.push(`Read ${readCount} file${readCount > 1 ? "s" : ""}`);
  if (editCount > 0)
    parts.push(`Edited ${editCount} file${editCount > 1 ? "s" : ""}`);
  if (bashCount > 0)
    parts.push(`Ran ${bashCount} command${bashCount > 1 ? "s" : ""}`);
  if (otherCount > 0)
    parts.push(`Used ${otherCount} tool${otherCount > 1 ? "s" : ""}`);

  return parts.join(", ");
}

/**
 * Short, human action label for the live phase activity line, e.g.
 * "writing KioskProvider.tsx" / "reading sheet.tsx" / "running a command".
 * Category-based, so it reads the same across both agent engines.
 */
function getToolActionLabel(tool: ActiveTool): string {
  const filePath =
    (tool.toolInput?.file_path as string) ||
    (tool.toolInput?.path as string) ||
    (tool.toolInput?.filePath as string) ||
    "";
  const base = filePath.split("/").filter(Boolean).pop();
  const category = getToolCategory(tool.toolName);
  if (category === "edit") return base ? `writing ${base}` : "writing files";
  if (category === "read") return base ? `reading ${base}` : "reading files";
  if (category === "bash") return "running a command";
  return getToolDisplayName(tool.toolName, tool.toolInput);
}

interface CollapsibleToolGroupProps {
  tools: ActiveTool[];
  isLast: boolean;
  isStreaming: boolean;
}

/**
 * Collapsible tool group - shows summary when collapsed, details when expanded
 */
const CollapsibleToolGroup = memo(function CollapsibleToolGroup({
  tools,
  isLast,
  isStreaming,
}: CollapsibleToolGroupProps) {
  // Collapsed by default — including the last group while streaming. The phase
  // timeline's live shimmer node already shows the current action, so an
  // auto-expanded tool group is just noise. Users click to expand any group.
  const [isExpanded, setIsExpanded] = useState(false);
  // Per-tool captured-screenshot reveal (collapsed by default) + zoom overlay.
  const [openImageTools, setOpenImageTools] = useState<Set<string>>(new Set());
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);

  const toggleImage = (toolUseId: string): void => {
    setOpenImageTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolUseId)) next.delete(toolUseId);
      else next.add(toolUseId);
      return next;
    });
  };

  const SKILLS_PATH_PATTERN = /\.claude\/skills\/([^/]+)\/SKILL\.md$/;

  // All Skill() tool calls — both base skills and marketplace skills
  const skillToolEntries = tools
    .filter((t) => t.toolName === "Skill")
    .map((t) => {
      const raw =
        t.toolInput?.skill ?? t.toolInput?.name ?? t.toolInput?.skill_name;
      if (typeof raw !== "string") return null;
      const isMarketplace = raw.startsWith(SKILL_PREFIX);
      const slug = isMarketplace ? raw.replace(SKILL_PREFIX, "") : raw;
      return { slug, status: t.status, isBaseSkill: !isMarketplace };
    })
    .filter(
      (s): s is { slug: string; status: ToolStatus; isBaseSkill: boolean } =>
        s !== null,
    );

  // Legacy: Read tool calls that read SKILL.md files directly
  const readSkillEntries = tools
    .filter((t) => t.toolName === "Read")
    .map((t) => {
      const filePath = t.toolInput?.file_path ?? t.toolInput?.path;
      if (typeof filePath !== "string") return null;
      const match = filePath.match(SKILLS_PATH_PATTERN);
      if (!match) return null;
      const dirName = match[1];
      const isBase = !dirName.startsWith("optidev-market-");
      return { slug: dirName, status: t.status, isBaseSkill: isBase };
    })
    .filter(
      (s): s is { slug: string; status: ToolStatus; isBaseSkill: boolean } =>
        s !== null,
    );

  // Combine, deduplicate by slug (Skill() call takes precedence over Read)
  const skillMap = new Map<
    string,
    { status: ToolStatus; isBaseSkill: boolean }
  >();
  [...readSkillEntries, ...skillToolEntries].forEach((s) =>
    skillMap.set(s.slug, { status: s.status, isBaseSkill: s.isBaseSkill }),
  );
  const activeMarketplaceSkills = [...skillMap.entries()].map(
    ([slug, { status, isBaseSkill }]) => ({ slug, status, isBaseSkill }),
  );

  // Filter out all skill-related tool calls (they get their own always-visible cards)
  const nonSkillTools = tools.filter((t) => {
    if (t.toolName === "Skill") return false;
    if (t.toolName === "Read") {
      const filePath = t.toolInput?.file_path ?? t.toolInput?.path;
      if (typeof filePath === "string" && SKILLS_PATH_PATTERN.test(filePath))
        return false;
    }
    return true;
  });

  const allCompleted = tools.every((t) => t.status === ToolStatus.COMPLETED);
  const hasError = nonSkillTools.some((t) => t.status === ToolStatus.ERROR);
  const hasRunning = tools.some((t) => t.status === ToolStatus.RUNNING);
  const errorCount = nonSkillTools.filter(
    (t) => t.status === ToolStatus.ERROR,
  ).length;
  const successCount = nonSkillTools.filter(
    (t) => t.status === ToolStatus.COMPLETED,
  ).length;
  const isPartialSuccess = hasError && successCount > 0;
  const summary = getToolGroupSummary(nonSkillTools);
  const hasNonSkillTools = nonSkillTools.length > 0;

  if (tools.length === 0) return null;

  return (
    <div className="mt-2">
      {/* Skill loaded cards — always visible (like Claude Code CLI) */}
      {activeMarketplaceSkills.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {activeMarketplaceSkills.map(({ slug, status, isBaseSkill }) => (
            <SkillCard
              key={slug}
              slug={slug}
              status={status}
              isBaseSkill={isBaseSkill}
            />
          ))}
        </div>
      )}

      {/* Summary row - only show if there are non-skill tools */}
      {hasNonSkillTools && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "group inline-flex items-center gap-1 text-xs transition-colors text-muted-foreground hover:text-foreground",
            activeMarketplaceSkills.length > 0 && "mt-1",
            hasError &&
              !isPartialSuccess &&
              "text-amber-600 dark:text-amber-500",
            isPartialSuccess && "text-amber-600 dark:text-amber-500",
            hasRunning && "text-blue-500",
          )}
        >
          {hasRunning ? (
            <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
          ) : hasError ? (
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
          ) : allCompleted ? (
            <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
          ) : null}

          <span className="hover:underline">
            {summary}
            {hasError && !hasRunning && (
              <span className="ml-1 text-amber-600 dark:text-amber-500 group-hover:text-inherit">
                ({errorCount} failed)
              </span>
            )}
          </span>

          {isExpanded ? (
            <ChevronDown className="w-3 h-3 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
          )}
        </button>
      )}

      {/* Expanded details */}
      {isExpanded && hasNonSkillTools && (
        <div className="mt-1 ml-1 space-y-1 border-l-2 border-border/30 pl-2">
          {nonSkillTools.map((tool) => {
            const displayName = getToolDisplayName(
              tool.toolName,
              tool.toolInput,
            );
            const filePath = getToolFilePath(tool.toolName, tool.toolInput);

            const captureImage =
              tool.status === ToolStatus.COMPLETED && tool.imageBase64
                ? `data:${tool.imageMimeType || "image/jpeg"};base64,${tool.imageBase64}`
                : null;

            const imageOpen = openImageTools.has(tool.toolUseId);

            return (
              <div key={tool.toolUseId}>
                <div
                  className={cn(
                    "flex items-center gap-2 px-2 py-1 rounded text-xs",
                    tool.status === ToolStatus.ERROR &&
                      "text-red-600 dark:text-red-400",
                    captureImage && "cursor-pointer hover:bg-muted/50",
                  )}
                  onClick={
                    captureImage
                      ? () => toggleImage(tool.toolUseId)
                      : undefined
                  }
                >
                  {tool.status === ToolStatus.RUNNING && (
                    <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                  )}
                  {tool.status === ToolStatus.COMPLETED && (
                    <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                  )}
                  {tool.status === ToolStatus.ERROR && (
                    <Minus className="w-3 h-3 text-red-500 flex-shrink-0" />
                  )}

                  <span className="font-medium text-foreground/80">
                    {displayName}
                  </span>

                  {filePath && (
                    <span
                      className="text-muted-foreground truncate max-w-[280px]"
                      title={filePath}
                    >
                      {shortenPath(filePath)}
                    </span>
                  )}

                  {/* Toggle affordance for the captured screenshot. */}
                  {captureImage &&
                    (imageOpen ? (
                      <ChevronDown className="w-3 h-3 ml-auto flex-shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0 text-muted-foreground" />
                    ))}
                </div>

                {/* Screenshot — collapsed by default; click row to reveal, click image to zoom. */}
                {captureImage && imageOpen && (
                  <button
                    type="button"
                    onClick={() => setZoomSrc(captureImage)}
                    className="block ml-5 mt-1 mb-1 w-fit"
                    title="Click to zoom"
                  >
                    <img
                      src={captureImage}
                      alt="Captured page"
                      className="max-h-28 max-w-[200px] rounded border border-border object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Zoom overlay for captured screenshots (data URLs can't open in a new tab). */}
      <ImageLightbox
        src={zoomSrc}
        alt="Captured page (full size)"
        onClose={() => setZoomSrc(null)}
      />
    </div>
  );
});

/**
 * Text-tool segment for inline rendering
 */
export interface TextToolSegment {
  text: string;
  tools: ActiveTool[];
}

/**
 * Extract @project:Name references from message content
 * Returns the cleaned content and an array of project names
 */
interface MentionRef {
  type: "project" | "file" | "theme" | "skill" | "asset";
  name: string;
  id?: string;
}

/**
 * Extract @project:Name[id], @file:path, and @theme:name from message content.
 */
function extractMentionRefs(content: string): {
  cleanContent: string;
  mentions: MentionRef[];
} {
  const mentions: MentionRef[] = [];

  // @project:Name[id] or @project:Name
  const projectPattern = /@project:([^\s,\[]+)(?:\[([^\]]+)\])?/g;
  let match;
  while ((match = projectPattern.exec(content)) !== null) {
    mentions.push({ type: "project", name: match[1], id: match[2] });
  }

  // @file:path/to/file.tsx
  const filePattern = /@file:([^\s,]+)/g;
  while ((match = filePattern.exec(content)) !== null) {
    mentions.push({ type: "file", name: match[1] });
  }

  // @theme:ThemeName
  const themePattern = /@theme:([^\s,]+)/g;
  while ((match = themePattern.exec(content)) !== null) {
    mentions.push({ type: "theme", name: match[1] });
  }

  // @skill:slug — format slug as readable display name
  const skillPattern = /@skill:([^\s,]+)/g;
  while ((match = skillPattern.exec(content)) !== null) {
    const rawSlug = match[1];
    const displayName = rawSlug
      .replace(/^custom-/, "")
      .split("-")
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    mentions.push({ type: "skill", name: displayName, id: rawSlug });
  }

  // @asset:<assetId>[<name>] — assetId first (no spaces), name in brackets
  // (allows spaces). Display the bracketed name, key by the id.
  const assetPattern = /@asset:([^\s,\[]+)(?:\[([^\]]+)\])?/g;
  while ((match = assetPattern.exec(content)) !== null) {
    mentions.push({ type: "asset", name: match[2] || match[1], id: match[1] });
  }

  const cleanContent = content
    .replace(projectPattern, "")
    .replace(filePattern, "")
    .replace(themePattern, "")
    .replace(skillPattern, "")
    .replace(assetPattern, "")
    .replace(/^\s+/, "")
    .trim();

  return { cleanContent, mentions };
}

/**
 * Strip self-contained `[SYSTEM: ...]` directives from a user-message before
 * display. The full directive still reaches Claude (it's part of the prompt)
 * but the chat UI hides it so users don't see implementation noise like
 * "...env vars injected into .env.local". Matches single-line balanced
 * brackets; multi-line blocks are out of scope.
 */
function stripSystemDirectives(content: string): string {
  return content.replace(/\s*\[SYSTEM:[^\]]*\]\s*/g, "").trim();
}

/**
 * Parse message content and tools into segments for inline display
 * Groups tools by their textPosition and interleaves with text chunks
 */
function parseTextToolSegments(
  content: string,
  tools: ActiveTool[],
): TextToolSegment[] {
  if (!tools || tools.length === 0) {
    return [{ text: content, tools: [] }];
  }

  // Get unique text positions, sorted
  const positions = [
    ...new Set(tools.map((t) => t.textPosition ?? content.length)),
  ].sort((a, b) => a - b);

  // Group tools by position
  const toolsByPosition = new Map<number, ActiveTool[]>();
  tools.forEach((tool) => {
    const pos = tool.textPosition ?? content.length;
    if (!toolsByPosition.has(pos)) {
      toolsByPosition.set(pos, []);
    }
    toolsByPosition.get(pos)!.push(tool);
  });

  // Create segments
  const segments: TextToolSegment[] = [];
  let lastPos = 0;

  positions.forEach((pos) => {
    // Text before this tool group (trim to remove \n\n separators)
    const text = content.slice(lastPos, pos).trim();
    const toolsAtPos = toolsByPosition.get(pos) || [];

    // Only add segment if there's text or tools
    if (text || toolsAtPos.length > 0) {
      segments.push({ text, tools: toolsAtPos });
    }
    lastPos = pos;
  });

  // Remaining text after last tool position
  if (lastPos < content.length) {
    const remainingText = content.slice(lastPos).trim();
    if (remainingText) {
      segments.push({ text: remainingText, tools: [] });
    }
  }

  return segments;
}

type ToolSubGroup = { tools: ActiveTool[]; cardTool: ActiveTool | null };

/**
 * Split a segment's tools so each HITL-card-bearing tool ENDS a sub-group; the
 * inline card then renders right after that sub-group, placing it directly under
 * its producing tool even when other tools were called in the same segment.
 * Tools without a HITL payload collapse into a single sub-group (unchanged).
 */
function splitToolsAtHitl(tools: ActiveTool[]): ToolSubGroup[] {
  const groups: ToolSubGroup[] = [];
  let buf: ActiveTool[] = [];
  for (const t of tools) {
    buf.push(t);
    if (t.hitlPayloadContent) {
      groups.push({ tools: buf, cardTool: t });
      buf = [];
    }
  }
  if (buf.length) groups.push({ tools: buf, cardTool: null });
  return groups;
}

export const AgentMessage = memo(function AgentMessage({
  message,
  onOpenFile,
  onAction,
  showHeader = true,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
  onBrowseThemes,
  onConnectionCreated,
  onCacheTtlSet,
  devContainerId,
  previewUrl,
  isStreamingTurn = false,
  awaitsUserInput = false,
}: AgentMessageProps) {
  const isUser = message.type === MessageType.USER;
  const isError = message.type === MessageType.ERROR;
  const isTool = message.type === MessageType.TOOL_USE;
  const isSystem = message.type === MessageType.SYSTEM;
  const { getIdToken } = useAuth();

  // Track which images have failed and their refreshed URLs
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [refreshedUrls, setRefreshedUrls] = useState<Record<string, string>>(
    {},
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Full-size preview for a clicked image attachment (null = closed).
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // Determine if this message is currently streaming (status is running and not completed)
  const isStreaming =
    isStreamingTurn || message.status === TaskStatus.RUNNING;

  // Only peel a trailing "closing" phase when the turn ended NATURALLY. A
  // stopped turn (status forced to COMPLETED, see `stopped`) or an errored one
  // has no genuine closing message — splitting would mislabel the interrupted
  // phase as "Wrap-up" / "Over to you".
  const canSplitClosing =
    !isStreaming &&
    message.status !== TaskStatus.ERROR &&
    message.stopped !== true;

  // Extract @project/@file/@theme mentions from user messages
  const mentionRefs = useMemo(() => {
    if (!isUser || !message.content) return null;
    const result = extractMentionRefs(message.content);
    return result.mentions.length > 0 ? result : null;
  }, [isUser, message.content]);

  // User-visible content with `[SYSTEM: ...]` directives stripped. Composes on
  // top of mention cleaning so both transformations apply.
  const displayContent = useMemo(() => {
    if (!isUser || !message.content) return message.content;
    const afterMentions = mentionRefs ? mentionRefs.cleanContent : message.content;
    return stripSystemDirectives(afterMentions);
  }, [isUser, message.content, mentionRefs]);

  // Parse visual editor message for clean display
  const visualEditorData = useMemo(() => {
    if (!isUser || !message.content) return null;
    return parseVisualEditorMessage(displayContent);
  }, [isUser, message.content, displayContent]);

  // Parse content + tools into segments for inline rendering
  // parseTextToolSegments handles undefined textPosition by placing at end (t.textPosition ?? content.length)
  const segments = useMemo(() => {
    if (
      message.type !== MessageType.ASSISTANT ||
      !message.activeTools?.length
    ) {
      return null;
    }
    return parseTextToolSegments(message.content || "", message.activeTools);
  }, [message.content, message.activeTools, message.type]);

  // Group the turn's segments into named phases. Primary source = the agent's
  // own TodoWrite list (agent-authored labels); heuristic tool-buckets fall back
  // when a turn has no todos. Engine-agnostic: derived from the normalized stream.
  const phaseResult = useMemo(
    () =>
      segments
        ? groupSegmentsIntoPhases(segments, { canSplitClosing, awaitsUserInput })
        : { phases: [] as Phase[], source: "none" as const },
    [segments, canSplitClosing, awaitsUserInput],
  );
  const phases = phaseResult.phases;
  // Body prose is muted for every step EXCEPT the current/last one — the
  // expanded, in-flight step. So segments at/after the LAST phase's start stay
  // bright; earlier, auto-collapsed phases dim. This is what keeps streaming text
  // readable (the live phase is the last phase) and only mutes a step once a new
  // one begins and it collapses. The completed closing node is also the last
  // phase, so its recap stays bright too.
  const brightFromIndex = useMemo(
    () => phases[phases.length - 1]?.startIndex ?? Infinity,
    [phases],
  );
  // When to show the timeline:
  //  - todo source → live AND completed (≥1 phase). The agent's todo grouping is
  //    append-only, so it grows safely while streaming — the main use case.
  //  - heuristic source → completed turns only (≥2 phases). Its look-ahead seed
  //    can relabel retroactively, so it isn't safe to stream live.
  // Show the timeline whenever there's at least one phase AND either the turn is
  // still streaming (live progress is the whole point — the heuristic baseline is
  // now stream-stable, so this is safe) or, for a finished turn, there are ≥2
  // phases (a single-phase finished turn reads better flat). Works for both
  // sources: heuristic drives the live window; todos take over with nicer labels
  // once they resolve.
  const usePhases =
    !isUser &&
    phaseGroupingEnabled() &&
    segments !== null &&
    phases.length >= 1 &&
    (isStreaming || phases.length >= 2);

  // Phase grouping splits segments at todo transitions, so the phase view's
  // segment count differs from the flat `segments`. Use the right total so the
  // streaming cursor + bottom spacing land on the actual last segment.
  const phaseSegmentTotal = useMemo(
    () => phases.reduce((n, p) => n + p.segments.length, 0),
    [phases],
  );

  // Render a single text+tools segment. Extracted so both the flat list and the
  // phase groups render segments identically. `globalIdx` is the segment's index
  // in the full turn so "is last overall" (drives streaming + spacing) is correct
  // even when a segment lives inside a collapsed phase.
  const renderSegment = (segment: TextToolSegment, globalIdx: number) => {
    if (!segment.text && segment.tools.length === 0) return null;
    const totalSegments = usePhases
      ? phaseSegmentTotal
      : (segments?.length ?? 0);
    return (
      <div key={globalIdx}>
        {/* Text chunk */}
        {segment.text && (
          <MarkdownRenderer
            content={segment.text}
            isStreaming={isStreaming && globalIdx === totalSegments - 1}
            className={cn(
              "text-sm",
              // In the phase timeline, dim the body prose of COMPLETED (collapsed)
              // steps so each bright title reads as a section header; keep
              // bold/key terms legible. The current/last step — the live
              // streaming phase or the concluding node — stays bright.
              // Links force `!text-foreground`, so override them with `!` too.
              usePhases &&
                globalIdx < brightFromIndex &&
                "[&_p]:text-muted-foreground [&_li]:text-muted-foreground [&_strong]:text-foreground [&_a]:!text-muted-foreground",
              isUser && "[&_*]:text-zinc-900 dark:[&_*]:text-zinc-50",
              isError && "[&_*]:text-destructive",
            )}
          />
        )}
        {/* Tools after this text chunk. Split the group at any HITL-card-bearing
            tool so its card (theme/media) renders directly beneath that tool. */}
        {segment.tools.length > 0 &&
          splitToolsAtHitl(segment.tools).map((g, gi, arr) => {
            const isLastOverall =
              globalIdx === totalSegments - 1 && gi === arr.length - 1;
            const inlineParsed = g.cardTool?.hitlPayloadContent
              ? extractUIPayload(g.cardTool.hitlPayloadContent)
              : null;
            return (
              <div key={`tg-${globalIdx}-${gi}`}>
                <div className={cn("tool-block", !isLastOverall && "mb-2")}>
                  <CollapsibleToolGroup
                    tools={g.tools}
                    isLast={isLastOverall}
                    isStreaming={isStreaming}
                  />
                </div>
                {g.cardTool && inlineParsed?.payload && (
                  <div
                    key={`hitl-${g.cardTool.toolUseId}`}
                    // Render the card flush (full width) under its tool group.
                    className="mt-2 mb-3"
                  >
                    <ClaudeResponseCard
                      payload={inlineParsed.payload}
                      markdown={inlineParsed.markdown}
                      messageId={g.cardTool.hitlMessageId}
                      hitlToolUseId={g.cardTool.toolUseId}
                      hitlTimestamp={g.cardTool.hitlMessageTimestamp}
                      onOpenFile={onOpenFile}
                      onAction={onAction}
                      workspaceId={workspaceId}
                      previewIframe={previewIframe}
                      projectId={projectId}
                      onEditTheme={onEditTheme}
                      onThemeSaved={onThemeSaved}
                      onBrowseThemes={onBrowseThemes}
                      onConnectionCreated={onConnectionCreated}
                      devContainerId={devContainerId}
                      previewUrl={previewUrl}
                      isStreaming={isStreaming}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  // Refresh all images in this message
  const handleRefreshImages = async () => {
    if (!message.files || isRefreshing) return;

    setIsRefreshing(true);

    try {
      const token = await getIdToken();

      // Fetch fresh presigned URLs for all files
      const refreshPromises = message.files.map(async (file) => {
        try {
          const response = await axios.get(
            `/api/files/${file.refId}/download`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          return { refId: file.refId, url: response.data.url };
        } catch (error) {
          console.error(
            `[AgentMessage] Failed to refresh ${file.refId}:`,
            error,
          );
          return null;
        }
      });

      const results = await Promise.all(refreshPromises);

      // Update all URLs and clear errors
      const newUrls: Record<string, string> = {};
      results.forEach((result) => {
        if (result) {
          newUrls[result.refId] = result.url;
        }
      });

      setRefreshedUrls((prev) => ({ ...prev, ...newUrls }));
      setImageErrors({}); // Clear all errors
      console.log("[AgentMessage] Refreshed all images");
    } catch (error) {
      console.error("[AgentMessage] Failed to refresh images:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleImageError = (refId: string) => {
    setImageErrors((prev) => ({ ...prev, [refId]: true }));
  };

  // Parse UI payload for assistant messages - check during streaming too
  const parsedResponse =
    message.type === MessageType.ASSISTANT
      ? extractUIPayload(message.content)
      : null;
  const hasUIPayload = parsedResponse?.payload !== null;

  // Check if payload is complete (has closing tag) - important for streaming
  const isPayloadComplete = message.content.includes("</ui_payload>");

  // Check if message has started a ui_payload but not finished
  const isStreamingPayload =
    message.type === MessageType.ASSISTANT &&
    message.content.includes("<ui_payload>") &&
    !isPayloadComplete;

  // Hide streaming content to prevent showing Claude's thinking text
  if (isStreamingPayload) {
    return null; // Return null - the "Thinking..." indicator in AgentChat will show instead
  }

  // OptiDev Cloud activation morphs the card in place — must run BEFORE the
  // ui_payload card render below, since the activate card carries a ui_payload
  // and would otherwise render its (stale) clicked state instead of the progress.
  // `activation` is live state while activating; on refresh it's gone, so we
  // derive a completed ("ready") state from the persisted clickedActions of the
  // activate card — keeping the green "activated" card visible across reloads.
  const activationStatus: ActivationStatus | undefined =
    message.activation ??
    (parsedResponse?.payload?.hitl_type === "activate_optidev_cloud" &&
    (message.clickedActions || []).includes("activate_optidev_cloud")
      ? "ready"
      : undefined);

  if (activationStatus) {
    return (
      <div className="p-3">
        <OptiDevCloudActivating status={activationStatus} />
      </div>
    );
  }

  // If assistant message has COMPLETE UI payload, render the clean card
  if (
    message.type === MessageType.ASSISTANT &&
    hasUIPayload &&
    isPayloadComplete &&
    parsedResponse?.payload
  ) {
    // HITL cards (Start Building, plan approval, publish, activate, connection
    // setup, etc.) arrive as their own message that lands directly under the
    // turn's closing "Over to you" phase node. That node's body is indented past
    // the timeline connector rail (message p-2.5 + content pl-2.5 − phases
    // -ml-3.5 + body pl-8 = 38px from the row). Match that left edge so every
    // card lines up under the step content above it instead of breaking out to
    // the left at the default p-3 inset.
    return (
      <div className="pt-0.5 pb-3 pr-3 pl-[38px]">
        <ClaudeResponseCard
          payload={parsedResponse.payload}
          markdown={parsedResponse.markdown}
          messageId={message.id}
          hitlTimestamp={message.timestamp}
          onOpenFile={onOpenFile}
          onAction={onAction}
          clickedActions={message.clickedActions}
          isStale={message.isStale}
          onViewCode={() => console.log("View code")}
          onRestore={() => console.log("Restore")}
          workspaceId={workspaceId}
          previewIframe={previewIframe}
          projectId={projectId}
          onEditTheme={onEditTheme}
          onThemeSaved={onThemeSaved}
          onBrowseThemes={onBrowseThemes}
          onConnectionCreated={onConnectionCreated}
          onCacheTtlSet={onCacheTtlSet}
          devContainerId={devContainerId}
          previewUrl={previewUrl}
          isStreaming={isStreaming}
        />
      </div>
    );
  }

  // Reduce padding for consecutive assistant messages (no header shown)
  const isConsecutiveAssistant = !isUser && !showHeader;

  return (
    <div
      className={cn(
        isConsecutiveAssistant ? "px-2.5 pt-0 pb-2.5" : "p-2.5",
        isUser && "flex flex-col items-end",
      )}
    >
      {/* Timestamp before user message */}
      {isUser && (
        <span className="text-xs text-muted-foreground mb-1">
          {formatTimestamp(message.timestamp)}
        </span>
      )}

      {/* Loading state for placeholder message */}
      {message.isLoading && (
        <div className="pl-2.5 py-2.5">
          <ThinkingIndicator />
        </div>
      )}

      {/* OptiDev Cloud activation progress card */}
      {message.activation && (
        <div className="pl-2.5 py-2.5">
          <OptiDevCloudActivating status={message.activation} />
        </div>
      )}

      {/* Message content - hide if loading or showing activation progress (except system messages which always show) */}
      {(!message.isLoading || isSystem) && !message.activation && (
        <div className={cn(isUser && "max-w-[85%] min-w-0")}>
          <div
            className={cn(
              "py-2.5 rounded-xl break-words whitespace-pre-wrap",
              isUser &&
                "bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50 border border-zinc-200 dark:border-zinc-700 px-3.5",
              isError &&
                "bg-destructive/10 border border-destructive/20 px-3.5",
              isSystem &&
                "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 px-3.5 text-center",
              !isUser && !isError && !isSystem && "pl-2.5",
            )}
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {isTool && message.metadata?.toolName && (
              <Badge variant="outline" className="text-xs mb-2">
                {message.metadata.toolName}
              </Badge>
            )}

            {message.metadata?.files && message.metadata.files.length > 0 && (
              <Badge variant="secondary" className="text-xs mb-2">
                {message.metadata.files.length} file
                {message.metadata.files.length > 1 ? "s" : ""} modified
              </Badge>
            )}

            {/* Render content with inline tools when segments available */}
            {segments ? (
              usePhases ? (
                // Phase-grouped rendering: a quiet build-pipeline timeline. The
                // rail (drawn inside each PhaseGroup) is the structure — no box.
                // -ml-3.5 pulls the rail/nodes/labels left into the parent's
                // padding so the timeline doesn't float with a wide left gutter.
                <div className="-ml-3.5 py-0.5">
                  {phases.map((phase, pi) => {
                    const isLivePhase =
                      isStreaming && pi === phases.length - 1;
                    const runningTool = isLivePhase
                      ? phase.segments
                          .flatMap((s) => s.tools)
                          .find((t) => t.status === ToolStatus.RUNNING)
                      : undefined;
                    return (
                      <PhaseGroup
                        key={phase.id}
                        phase={phase}
                        isFirst={pi === 0}
                        isLast={pi === phases.length - 1}
                        isLivePhase={isLivePhase}
                        liveActionLabel={
                          runningTool
                            ? getToolActionLabel(runningTool)
                            : undefined
                        }
                        renderSegment={renderSegment}
                      />
                    );
                  })}
                </div>
              ) : (
                // Flat segment-based rendering: text + tools interleaved
                <div className="space-y-0">
                  {segments.map((segment, idx) => renderSegment(segment, idx))}
                </div>
              )
            ) : (
              // Fallback: simple content rendering (no tools or non-assistant)
              message.content && (
                <>
                  {/* Visual Editor Command - Clean Display */}
                  {visualEditorData ? (
                    <div className="space-y-2.5">
                      {/* Command text */}
                      <MarkdownRenderer
                        content={visualEditorData.command}
                        isStreaming={isStreaming}
                        className={cn(
                          "text-sm",
                          isUser &&
                            "[&_*]:text-zinc-900 dark:[&_*]:text-zinc-50",
                        )}
                      />

                      {/* Element info badge */}
                      {visualEditorData.elementContext && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-foreground/10 border border-primary-foreground/20">
                          <span className="text-[11px] font-medium opacity-90">
                            {getElementSummary(visualEditorData.elementContext)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular message (with mention badges if present)
                    <>
                      {mentionRefs && mentionRefs.mentions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {mentionRefs.mentions.map((ref, idx) => (
                            <MentionBadge
                              key={`${ref.type}-${ref.id || ref.name}-${idx}`}
                              type={ref.type}
                              name={ref.name}
                              id={ref.id}
                              variant="onPrimary"
                            />
                          ))}
                        </div>
                      )}
                      <MarkdownRenderer
                        content={
                          isUser
                            ? displayContent
                            : mentionRefs
                              ? mentionRefs.cleanContent
                              : message.content
                        }
                        isStreaming={isStreaming}
                        className={cn(
                          "text-sm",
                          isUser &&
                            "[&_*]:text-zinc-900 dark:[&_*]:text-zinc-50",
                          isError && "[&_*]:text-destructive",
                        )}
                      />
                    </>
                  )}
                </>
              )
            )}

            {/* Tool usage pills are now rendered inline with the last tool segment above */}
            {/* Removed fallback to prevent duplication when activeTools is missing */}

            {/* File attachments from database */}
            {message.files && message.files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.files.map((file) => {
                  const isImage = file.mimeType.startsWith("image/");
                  const isPDF = file.mimeType.includes("pdf");
                  const isVideo = file.mimeType.startsWith("video/");

                  return (
                    <div
                      key={file.id}
                      className="group relative h-20 w-20 overflow-hidden rounded-xl border border-border bg-muted transition-all hover:border-primary/40 hover:shadow-md hover:shadow-black/5"
                    >
                      {/* Image/thumbnail preview or file icon */}
                      {isImage ? (
                        <div className="relative h-full w-full">
                          <img
                            src={
                              refreshedUrls[file.refId] ||
                              file.thumbnailUrl ||
                              file.downloadUrl
                            }
                            alt={file.originalName}
                            data-testid="chat-attachment-image"
                            className="h-full w-full cursor-pointer object-cover transition-transform duration-300 group-hover:scale-105"
                            onClick={() =>
                              setLightboxSrc(
                                file.downloadUrl || file.assetUrl,
                              )
                            }
                            onError={() => handleImageError(file.refId)}
                          />
                          {/* Expand affordance — signals click-to-preview. Decorative
                              (pointer-events-none); the click is handled by the image. */}
                          <div className="pointer-events-none absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/30 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                            <Expand className="h-3 w-3" />
                          </div>
                          {/* Show refresh icon on broken images */}
                          {imageErrors[file.refId] && (
                            <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRefreshImages();
                                }}
                                disabled={isRefreshing}
                                className="rounded-full bg-muted p-2 transition-colors hover:bg-accent disabled:opacity-50"
                                title="Refresh image"
                              >
                                {isRefreshing ? (
                                  <Loader2 className="h-5 w-5 animate-spin text-foreground" />
                                ) : (
                                  <RefreshCw className="h-5 w-5 text-foreground" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-muted">
                          {isPDF ? (
                            <FileText className="h-8 w-8 text-muted-foreground" />
                          ) : isVideo ? (
                            <FileIcon className="h-8 w-8 text-muted-foreground" />
                          ) : (
                            <FileIcon className="h-8 w-8 text-muted-foreground" />
                          )}
                        </div>
                      )}

                      {/* Filename — gradient scrim caption; decorative, must not
                          swallow clicks meant for the image (pointer-events-none). */}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent px-1.5 pb-1 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
                        <span className="block truncate text-[10px] leading-tight text-white/90">
                          {file.originalName}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tool metadata files */}
            {message.metadata?.files && message.metadata.files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {message.metadata.files.map((file, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-muted-foreground"
                  >
                    <FileText className="w-3 h-3" />
                    {file}
                  </span>
                ))}
              </div>
            )}

            {/* Full-size preview for a clicked image attachment. */}
            <ImageLightbox
              src={lightboxSrc}
              onClose={() => setLightboxSrc(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
});
